/**
 * TrendingTickersCard - Shows top gainers, losers, and most active stocks.
 *
 * Adapted from TopMovers pattern but uses /market-pulse data (general market, not portfolio).
 */

import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TickerLogo } from '@/components/ui/TickerLogo';

export interface MarketMover {
  symbol: string;
  name: string;
  change_pct: number;
  price: number;
  volume: number;
}

interface TrendingTickersCardProps {
  gainers: MarketMover[];
  losers: MarketMover[];
  mostActive: MarketMover[];
  isLoading?: boolean;
  onTickerClick?: (symbol: string) => void;
}

function formatVolume(vol: number): string {
  if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B`;
  if (vol >= 1e6) return `${(vol / 1e6).toFixed(1)}M`;
  if (vol >= 1e3) return `${(vol / 1e3).toFixed(0)}K`;
  return vol.toString();
}

function MoverRow({ mover, rank, onTickerClick }: {
  mover: MarketMover;
  rank: number;
  onTickerClick?: (symbol: string) => void;
}) {
  const isPositive = mover.change_pct >= 0;
  return (
    <button
      onClick={() => onTickerClick?.(mover.symbol)}
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-surface-light/50 transition-colors text-left w-full"
    >
      <span className="text-[10px] text-text-muted w-3 text-center">{rank}</span>
      <TickerLogo symbol={mover.symbol} name={mover.name} size="xs" />
      <div className="flex-1 min-w-0">
        <span className="font-mono text-xs font-semibold text-text-primary">{mover.symbol}</span>
        {mover.name && (
          <p className="text-[10px] text-text-muted truncate">{mover.name}</p>
        )}
      </div>
      <span className={cn(
        'text-xs font-semibold tabular-nums',
        isPositive ? 'text-green-400' : 'text-red-400'
      )}>
        {isPositive ? '+' : ''}{mover.change_pct.toFixed(1)}%
      </span>
    </button>
  );
}

function VolumeRow({ mover, rank, onTickerClick }: {
  mover: MarketMover;
  rank: number;
  onTickerClick?: (symbol: string) => void;
}) {
  const isPositive = mover.change_pct >= 0;
  return (
    <button
      onClick={() => onTickerClick?.(mover.symbol)}
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-surface-light/50 transition-colors text-left w-full"
    >
      <span className="text-[10px] text-text-muted w-3 text-center">{rank}</span>
      <TickerLogo symbol={mover.symbol} size="xs" />
      <div className="flex-1 min-w-0">
        <span className="font-mono text-xs font-semibold text-text-primary">{mover.symbol}</span>
      </div>
      <span className="text-[10px] text-text-muted tabular-nums">{formatVolume(mover.volume)}</span>
      <span className={cn(
        'text-[10px] font-medium tabular-nums',
        isPositive ? 'text-green-400' : 'text-red-400'
      )}>
        {isPositive ? '+' : ''}{mover.change_pct.toFixed(1)}%
      </span>
    </button>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 animate-pulse">
          <div className="w-3 h-3 bg-surface-light rounded" />
          <div className="flex-1 h-3 bg-surface-light rounded" />
          <div className="w-12 h-3 bg-surface-light rounded" />
        </div>
      ))}
    </div>
  );
}

export function TrendingTickersCard({ gainers, losers, mostActive, isLoading, onTickerClick }: TrendingTickersCardProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Gainers */}
      <div className="rounded-xl border border-border bg-surface p-3">
        <div className="flex items-center gap-2 px-2 pb-2 border-b border-border mb-1">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">Top Gainers</span>
        </div>
        {isLoading ? <LoadingSkeleton /> : (
          gainers.length > 0 ? (
            gainers.slice(0, 5).map((m, i) => (
              <MoverRow key={m.symbol} mover={m} rank={i + 1} onTickerClick={onTickerClick} />
            ))
          ) : (
            <p className="text-xs text-text-muted text-center py-3">No data</p>
          )
        )}
      </div>

      {/* Losers */}
      <div className="rounded-xl border border-border bg-surface p-3">
        <div className="flex items-center gap-2 px-2 pb-2 border-b border-border mb-1">
          <TrendingDown className="w-4 h-4 text-red-400" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">Top Losers</span>
        </div>
        {isLoading ? <LoadingSkeleton /> : (
          losers.length > 0 ? (
            losers.slice(0, 5).map((m, i) => (
              <MoverRow key={m.symbol} mover={m} rank={i + 1} onTickerClick={onTickerClick} />
            ))
          ) : (
            <p className="text-xs text-text-muted text-center py-3">No data</p>
          )
        )}
      </div>

      {/* Most Active */}
      <div className="rounded-xl border border-border bg-surface p-3">
        <div className="flex items-center gap-2 px-2 pb-2 border-b border-border mb-1">
          <BarChart3 className="w-4 h-4 text-primary" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">Most Active</span>
        </div>
        {isLoading ? <LoadingSkeleton /> : (
          mostActive.length > 0 ? (
            mostActive.slice(0, 5).map((m, i) => (
              <VolumeRow key={m.symbol} mover={m} rank={i + 1} onTickerClick={onTickerClick} />
            ))
          ) : (
            <p className="text-xs text-text-muted text-center py-3">No data</p>
          )
        )}
      </div>
    </div>
  );
}
